import Placeholder from '@tiptap/extension-placeholder';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import PropTypes from 'prop-types';
import { BsTypeBold, BsTypeItalic, BsListUl, BsListOl } from 'react-icons/bs';

/**
 * Minimal WYSIWYG editor built on Tiptap. Supports bold, italic,
 * bullet list, ordered list. Outputs HTML.
 */

const NoteEditor = ({ value, onChange, placeholder = 'Notiz schreiben …' }) => {
	const editor = useEditor({
		extensions: [StarterKit, Placeholder.configure({ placeholder })],
		content: value || '',
		onUpdate: ({ editor: e }) => onChange(e.getHTML()),
		editorProps: {
			attributes: {
				class: 'rich-text-content max-w-none min-h-[80px] px-3 py-2 outline-none text-sm text-text',
			},
		},
	});

	if (!editor) return null;

	const btn = (active, onClick, children) => (
		<button
			type="button"
			onClick={onClick}
			className={`p-1 rounded-control transition-colors ${active ? 'bg-primary-100 text-primary-700' : 'text-bgBase-800 hover:text-text hover:bg-bgBase-400'}`}
		>
			{children}
		</button>
	);

	return (
		<div className="border border-bgBase-500 rounded-card overflow-hidden bg-bgBase-200">
			<div className="flex items-center gap-0.5 px-2 py-1 border-b border-bgBase-400 bg-bgBase-300/50">
				{btn(
					editor.isActive('bold'),
					() => editor.chain().focus().toggleBold().run(),
					<BsTypeBold className="w-3.5 h-3.5" />,
				)}
				{btn(
					editor.isActive('italic'),
					() => editor.chain().focus().toggleItalic().run(),
					<BsTypeItalic className="w-3.5 h-3.5" />,
				)}
				<div className="w-px h-4 bg-bgBase-500 mx-0.5" />
				{btn(
					editor.isActive('bulletList'),
					() => editor.chain().focus().toggleBulletList().run(),
					<BsListUl className="w-3.5 h-3.5" />,
				)}
				{btn(
					editor.isActive('orderedList'),
					() => editor.chain().focus().toggleOrderedList().run(),
					<BsListOl className="w-3.5 h-3.5" />,
				)}
			</div>
			<EditorContent editor={editor} />
		</div>
	);
};

NoteEditor.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
};

export default NoteEditor;
